import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  Activity,
  AlertCircle,
  Award,
  Bot,
  Building,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Edit,
  ExternalLink,
  Globe,
  Hash,
  Heart,
  Link as LinkIcon,
  Mail,
  MapPin,
  Newspaper,
  Phone,
  Plus,
  Save,
  Sparkles,
  Star,
  Target,
  Trash2,
  TrendingUp,
  X,
} from 'lucide-react';
import { api } from '../../api/client';
import EmptyState from '../../components/EmptyState';
import InfoTip from '../../components/InfoTip';
import ErrorBanner from '../../components/ErrorBanner';
import { SkeletonGrid, SkeletonList } from '../../components/Skeleton';
import { toast } from '../../lib/toast';
import { formatDate, formatRelative } from '../../lib/format';

const SOURCE_META = {
  'ai-engine': { label: 'AI engine', icon: Sparkles, tone: 'bg-brand-50 text-brand-700' },
  'review-platform': { label: 'Review', icon: Star, tone: 'bg-amber-50 text-amber-700' },
  directory: { label: 'Directory', icon: Building, tone: 'bg-cyan-500/10 text-cyan-700' },
  news: { label: 'News', icon: Newspaper, tone: 'bg-emerald-50 text-emerald-700' },
  partnership: { label: 'Partnership', icon: LinkIcon, tone: 'bg-rose-50 text-rose-700' },
  social: { label: 'Social', icon: TrendingUp, tone: 'bg-fuchsia-50 text-fuchsia-700' },
  other: { label: 'Other', icon: Globe, tone: 'bg-gray-100 text-gray-700' },
};

const AI_ENGINE_LABELS = {
  chatgpt: 'ChatGPT',
  claude: 'Claude',
  perplexity: 'Perplexity',
  gemini: 'Gemini',
  copilot: 'Copilot',
  other: 'Other',
};

const NAP_META = {
  match: { label: 'NAP match', class: 'bg-emerald-50 text-emerald-700' },
  partial: { label: 'NAP partial', class: 'bg-amber-50 text-amber-700' },
  mismatch: { label: 'NAP mismatch', class: 'bg-rose-50 text-rose-700' },
  na: { label: 'N/A', class: 'bg-gray-100 text-gray-600' },
  unknown: { label: 'NAP unknown', class: 'bg-gray-100 text-gray-600' },
};

const OPP_STATUS_META = {
  unclaimed: { label: 'Unclaimed', class: 'bg-gray-100 text-gray-700' },
  claimed: { label: 'Claimed', class: 'bg-amber-50 text-amber-700' },
  inProgress: { label: 'In progress', class: 'bg-cyan-500/10 text-cyan-700' },
  verified: { label: 'Verified', class: 'bg-emerald-50 text-emerald-700' },
  skipped: { label: 'Skipped', class: 'bg-gray-100 text-gray-500' },
};

const scoreTone = (s) =>
  s >= 70 ? 'emerald' : s >= 40 ? 'amber' : 'rose';
const scoreColorRing = (s) =>
  s >= 70 ? '#10b981' : s >= 40 ? '#f59e0b' : '#f43f5e';

export default function CitationsTab({ domain }) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('all');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const dashboardQuery = useQuery({
    queryKey: ['citation-dashboard', domain._id],
    queryFn: () =>
      api
        .get(`/api/domains/${domain._id}/citations/dashboard`)
        .then((r) => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (body) =>
      api
        .post(`/api/domains/${domain._id}/citations`, body)
        .then((r) => r.data.citation),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['citation-dashboard', domain._id] });
      setAdding(false);
      toast.success('Citation tracked');
    },
    onError: (err) => toast.error(err, 'Could not add citation'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }) =>
      api
        .put(`/api/domains/${domain._id}/citations/${id}`, body)
        .then((r) => r.data.citation),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['citation-dashboard', domain._id] });
      setEditingId(null);
      toast.success('Citation updated');
    },
    onError: (err) => toast.error(err, 'Could not update citation'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) =>
      api.delete(`/api/domains/${domain._id}/citations/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['citation-dashboard', domain._id] });
      toast.success('Citation removed');
    },
    onError: (err) => toast.error(err, 'Could not remove citation'),
  });

  const data = dashboardQuery.data;
  const summary = data?.summary;
  const hub = data?.hub;
  const allCitations = data?.citations || [];
  const visibleCitations =
    filter === 'all' ? allCitations : allCitations.filter((c) => c.source_type === filter);

  return (
    <div className="space-y-6">
      <AuthorityHero summary={summary} domain={domain} />

      {dashboardQuery.isLoading ? (
        <>
          <SkeletonGrid count={4} />
          <SkeletonList rows={5} />
        </>
      ) : (
        <>
          <SummaryTiles summary={summary} />

          <NapProfile domain={domain} hub={hub} />

          <div className="flex items-center justify-between flex-wrap gap-3">
            <SourceFilter
              filter={filter}
              setFilter={setFilter}
              bySource={summary?.bySource}
              total={allCitations.length}
            />
            <button
              className="btn-primary"
              onClick={() => {
                setAdding(true);
                setEditingId(null);
              }}
            >
              <Plus className="w-4 h-4" /> Add citation
            </button>
          </div>

          {adding && (
            <CitationForm
              onCancel={() => setAdding(false)}
              onSubmit={(body) => createMutation.mutate(body)}
              pending={createMutation.isPending}
              error={createMutation.error?.response?.data?.error}
            />
          )}

          {!allCitations.length ? (
            <EmptyState
              icon={Award}
              title="No citations tracked yet"
              body="Log each external mention of your brand — AI-engine citations, review-site listings, news articles, partner directories. Authority score is computed from URL signals and grows as your citation profile fills out."
              action={
                <button className="btn-primary" onClick={() => setAdding(true)}>
                  <Plus className="w-4 h-4" /> Add your first citation
                </button>
              }
            />
          ) : (
            <CitationsList
              citations={visibleCitations}
              editingId={editingId}
              setEditingId={setEditingId}
              updateMutation={updateMutation}
              deleteMutation={deleteMutation}
            />
          )}

          <Opportunities domain={domain} hub={hub} />
        </>
      )}
    </div>
  );
}

/* ------------ Authority hero ----------- */

function AuthorityHero({ summary, domain }) {
  const score = summary?.score ?? 0;
  const tone = scoreTone(score);
  const ring = scoreColorRing(score);
  const radius = 52;
  const circ = 2 * Math.PI * radius;
  const offset = circ * (1 - score / 100);

  return (
    <div className="relative rounded-2xl overflow-hidden bg-ink-gradient text-white p-6 sm:p-8 shadow-xl">
      <div className="absolute inset-0 opacity-30 bg-brand-radial" />
      <div className="absolute inset-0 bg-grid opacity-20" />
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-600 blur-3xl opacity-30" />
      <div className="relative flex items-center gap-6 flex-wrap">
        <svg width="120" height="120" viewBox="0 0 120 120" className="flex-none">
          <circle
            cx="60"
            cy="60"
            r={radius}
            stroke="rgba(255,255,255,0.12)"
            strokeWidth="10"
            fill="none"
          />
          <circle
            cx="60"
            cy="60"
            r={radius}
            stroke={ring}
            strokeWidth="10"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            transform="rotate(-90 60 60)"
            style={{ transition: 'stroke-dashoffset 600ms ease-out' }}
          />
          <text x="60" y="58" textAnchor="middle" fontSize="28" fontWeight="700" fill="#fff">
            {score}
          </text>
          <text
            x="60"
            y="78"
            textAnchor="middle"
            fontSize="9"
            letterSpacing="1.5"
            fill="rgba(255,255,255,0.65)"
          >
            AUTHORITY
          </text>
        </svg>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-white/60 mb-1 flex items-center gap-1.5">
            <Award className="w-3 h-3" /> Citation profile
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold inline-flex items-center gap-2">
            Authority score: {score}/100
            <span className="text-white/70">
              <InfoTip
                title="How authority is calculated"
                body="A 0–100 score weighted from per-citation authority (50% — how trusted each mention's domain is), NAP consistency (30% — does your business Name/Address/Phone match across listings?), and opportunity coverage (20% — how many high-value directories you've claimed)."
              />
            </span>
          </h2>
          <p className="text-sm text-white/70 mt-1 max-w-xl">
            Track mentions across AI engines, news, reviews, and directories for{' '}
            <strong className="text-white">{domain.domain}</strong>. A higher score
            means more authoritative external signals pointing to your brand.
          </p>
        </div>
      </div>
    </div>
  );
}

function SummaryTiles({ summary }) {
  if (!summary) return null;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Tile
        icon={LinkIcon}
        label="Citations tracked"
        value={summary.citationCount}
      />
      <Tile
        icon={TrendingUp}
        label="Avg authority"
        value={summary.avgAuthority}
        suffix=" / 100"
      />
      <Tile
        icon={Heart}
        label="NAP consistency"
        value={`${summary.napConsistency}%`}
      />
      <Tile
        icon={Target}
        label="Opportunities claimed"
        value={`${summary.claimedCount}/${summary.opportunityCount}`}
      />
    </div>
  );
}

function Tile({ icon: Icon, label, value, suffix = '' }) {
  return (
    <div className="card-compact relative overflow-hidden">
      <div className="absolute -top-8 -right-8 h-24 w-24 rounded-full bg-gradient-to-br from-brand-50 to-brand-100/40 opacity-60 blur-2xl pointer-events-none" />
      <div className="relative flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
            {label}
          </div>
          <div className="text-2xl font-bold text-ink-900 mt-0.5">
            {value}
            <span className="text-sm font-medium text-ink-400">{suffix}</span>
          </div>
        </div>
        <span className="flex-none h-9 w-9 rounded-lg bg-gradient-to-br from-brand-50 to-cyan-500/10 text-brand-700 flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </span>
      </div>
    </div>
  );
}

/* ------------ NAP profile ----------- */

function NapProfile({ domain, hub }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (hub && !form) {
      setForm({
        canonical_name: hub.canonical_name || '',
        canonical_address: hub.canonical_address || '',
        canonical_phone: hub.canonical_phone || '',
        canonical_email: hub.canonical_email || '',
        notes: hub.notes || '',
      });
    }
  }, [hub, form]);

  const updateMutation = useMutation({
    mutationFn: (body) =>
      api
        .put(`/api/domains/${domain._id}/citations/hub`, body)
        .then((r) => r.data.hub),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['citation-dashboard', domain._id] });
      toast.success('NAP profile saved');
    },
    onError: (err) => toast.error(err, 'Could not save NAP profile'),
  });

  if (!form) return null;
  const filled = [form.canonical_name, form.canonical_address, form.canonical_phone].filter(
    Boolean,
  ).length;

  return (
    <div className="card">
      <button
        className="section-header w-full hover:opacity-90"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <div className="section-title">
          <span className="section-icon">
            <MapPin className="w-4 h-4" />
          </span>
          NAP profile (Name · Address · Phone)
        </div>
        <span className="flex items-center gap-2 text-xs text-ink-400">
          {filled === 3 ? (
            <span className="pill-success">
              <CheckCircle2 className="w-3 h-3" /> Complete
            </span>
          ) : (
            <span className="pill-warn">
              {filled}/3 set
            </span>
          )}
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {open && (
        <div className="space-y-3 pt-3 border-t border-gray-100">
          <p className="text-sm text-ink-400">
            Your canonical Name / Address / Phone. AI engines and directories penalize
            inconsistencies — keep these values identical wherever you list them.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label">Business name</label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
                <input
                  className="input pl-10"
                  value={form.canonical_name}
                  onChange={(e) => setForm({ ...form, canonical_name: e.target.value })}
                  placeholder="Acme Wellness Spa"
                />
              </div>
            </div>
            <div>
              <label className="label">Phone</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
                <input
                  className="input pl-10"
                  value={form.canonical_phone}
                  onChange={(e) => setForm({ ...form, canonical_phone: e.target.value })}
                  placeholder="+1-555-123-4567"
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="label">Address</label>
              <textarea
                className="input min-h-[64px]"
                value={form.canonical_address}
                onChange={(e) => setForm({ ...form, canonical_address: e.target.value })}
                placeholder="123 Main St, Suite 4, Brooklyn, NY 11201, US"
              />
            </div>
            <div>
              <label className="label">Email (optional)</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
                <input
                  className="input pl-10"
                  type="email"
                  value={form.canonical_email}
                  onChange={(e) => setForm({ ...form, canonical_email: e.target.value })}
                  placeholder="hello@acme.com"
                />
              </div>
            </div>
            <div>
              <label className="label">Notes (optional)</label>
              <input
                className="input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Aliases, brand variations, etc."
              />
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap pt-1">
            <button
              className="btn-primary"
              onClick={() => updateMutation.mutate(form)}
              disabled={updateMutation.isPending}
            >
              <Save className="w-4 h-4" />
              {updateMutation.isPending ? 'Saving…' : 'Save NAP profile'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------ Source filter ----------- */

function SourceFilter({ filter, setFilter, bySource, total }) {
  const opts = [
    { id: 'all', label: 'All', count: total },
    ...Object.keys(SOURCE_META).map((k) => ({
      id: k,
      label: SOURCE_META[k].label,
      count: bySource?.[k] || 0,
    })),
  ];
  return (
    <div className="flex gap-1.5 overflow-x-auto scroll-soft py-1">
      {opts.map((o) => (
        <button
          key={o.id}
          onClick={() => setFilter(o.id)}
          className={clsx(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap transition-colors border',
            filter === o.id
              ? 'bg-brand-50 text-brand-700 border-brand-200'
              : 'bg-white text-ink-700 border-gray-200 hover:border-brand-200',
          )}
        >
          {o.label}
          <span className="text-ink-400">{o.count}</span>
        </button>
      ))}
    </div>
  );
}

/* ------------ Citations list ----------- */

function CitationsList({
  citations,
  editingId,
  setEditingId,
  updateMutation,
  deleteMutation,
}) {
  if (!citations.length) {
    return (
      <div className="card">
        <p className="text-sm text-ink-400">
          No citations match this filter. Try "All" or add a new one.
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <LinkIcon className="w-4 h-4" />
          </span>
          Tracked citations
        </div>
        <span className="text-xs text-ink-400">{citations.length}</span>
      </div>
      <ul className="divide-y divide-gray-50">
        {citations.map((c) =>
          editingId === c._id ? (
            <li key={c._id} className="py-4 first:pt-0 last:pb-0">
              <CitationForm
                initial={c}
                onCancel={() => setEditingId(null)}
                onSubmit={(body) => updateMutation.mutate({ id: c._id, body })}
                pending={
                  updateMutation.isPending && updateMutation.variables?.id === c._id
                }
                error={
                  updateMutation.isError && updateMutation.variables?.id === c._id
                    ? updateMutation.error?.response?.data?.error
                    : null
                }
              />
            </li>
          ) : (
            <CitationRow
              key={c._id}
              citation={c}
              onEdit={() => setEditingId(c._id)}
              onDelete={() => {
                if (window.confirm('Delete this citation record?')) {
                  deleteMutation.mutate(c._id);
                }
              }}
            />
          ),
        )}
      </ul>
    </div>
  );
}

function CitationRow({ citation, onEdit, onDelete }) {
  const meta = SOURCE_META[citation.source_type] || SOURCE_META.other;
  const Icon = meta.icon;
  const nap = NAP_META[citation.nap_match] || NAP_META.unknown;
  const score = citation.authority_score ?? 0;
  const tone = scoreTone(score);
  const scoreClass = {
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    rose: 'bg-rose-50 text-rose-600',
  }[tone];

  return (
    <li className="py-3 flex items-start justify-between gap-3 first:pt-0 last:pb-0">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <span
          className={clsx(
            'flex-none h-10 w-10 rounded-xl flex items-center justify-center',
            meta.tone,
          )}
        >
          <Icon className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className={`pill ${meta.tone}`}>{meta.label}</span>
            {citation.source_type === 'ai-engine' && citation.ai_engine && (
              <span className="pill-brand">
                <Bot className="w-3 h-3" /> {AI_ENGINE_LABELS[citation.ai_engine]}
              </span>
            )}
            <span className={`pill ${nap.class}`}>{nap.label}</span>
          </div>
          <div className="font-medium text-ink-900 truncate">
            {citation.source_name || hostname(citation.url) || citation.url}
          </div>
          <a
            href={citation.url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-brand-600 hover:underline break-all inline-flex items-center gap-1"
          >
            {citation.url} <ExternalLink className="w-3 h-3" />
          </a>
          {citation.source_type === 'ai-engine' && citation.prompt && (
            <div className="text-xs text-ink-400 mt-1 italic">
              Prompt: "{citation.prompt}"
            </div>
          )}
          <div className="text-xs text-ink-400 mt-1 flex flex-wrap gap-x-3 gap-y-1">
            <span>Observed {formatDate(citation.observed_at)}</span>
            {citation.page_cited && (
              <span className="inline-flex items-center gap-1 font-mono">
                <LinkIcon className="w-3 h-3" /> {citation.page_cited}
              </span>
            )}
            {citation.authority_signals?.knownAuthority && (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <Award className="w-3 h-3" /> Known-authority host
              </span>
            )}
          </div>
          {citation.notes && (
            <p className="text-xs text-ink-700 mt-1.5">{citation.notes}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-none">
        <span
          className={clsx(
            'flex-none h-10 w-10 rounded-xl flex items-center justify-center font-bold text-sm',
            scoreClass,
          )}
          title="Authority score"
        >
          {score}
        </span>
        <button className="btn-icon" title="Edit" onClick={onEdit}>
          <Edit className="w-4 h-4" />
        </button>
        <button
          className="btn-icon hover:!text-rose-600 hover:!bg-rose-50"
          title="Delete"
          onClick={onDelete}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </li>
  );
}

function CitationForm({ initial, onCancel, onSubmit, pending, error }) {
  const [form, setForm] = useState(() => ({
    url: initial?.url || '',
    source_type: initial?.source_type || 'ai-engine',
    ai_engine: initial?.ai_engine || 'chatgpt',
    prompt: initial?.prompt || '',
    source_name: initial?.source_name || '',
    page_cited: initial?.page_cited || '',
    observed_at: initial?.observed_at
      ? new Date(initial.observed_at).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10),
    nap_match: initial?.nap_match || 'unknown',
    notes: initial?.notes || '',
  }));
  const isAi = form.source_type === 'ai-engine';

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      ai_engine: isAi ? form.ai_engine : null,
      prompt: isAi ? form.prompt : '',
    });
  };

  return (
    <div className={clsx(initial ? '' : 'card')}>
      {!initial && (
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <Plus className="w-4 h-4" />
            </span>
            New citation
          </div>
          <button className="btn-icon" onClick={onCancel} title="Cancel">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="label">Citation URL</label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              type="url"
              required
              placeholder="https://example.com/article"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
            />
          </div>
        </div>

        <div>
          <label className="label">Source type</label>
          <select
            className="input"
            value={form.source_type}
            onChange={(e) => setForm({ ...form, source_type: e.target.value })}
          >
            {Object.entries(SOURCE_META).map(([id, meta]) => (
              <option key={id} value={id}>
                {meta.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">NAP consistency</label>
          <select
            className="input"
            value={form.nap_match}
            onChange={(e) => setForm({ ...form, nap_match: e.target.value })}
          >
            <option value="unknown">Unknown</option>
            <option value="match">Match</option>
            <option value="partial">Partial</option>
            <option value="mismatch">Mismatch</option>
            <option value="na">Not applicable</option>
          </select>
        </div>

        {isAi && (
          <>
            <div>
              <label className="label">AI engine</label>
              <select
                className="input"
                value={form.ai_engine || 'chatgpt'}
                onChange={(e) => setForm({ ...form, ai_engine: e.target.value })}
              >
                {Object.entries(AI_ENGINE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Prompt that surfaced the citation</label>
              <input
                className="input"
                placeholder='e.g. "best email tools for SaaS"'
                value={form.prompt}
                onChange={(e) => setForm({ ...form, prompt: e.target.value })}
              />
            </div>
          </>
        )}

        <div>
          <label className="label">Source name (optional)</label>
          <input
            className="input"
            placeholder="TechCrunch / G2 / ChatGPT"
            value={form.source_name}
            onChange={(e) => setForm({ ...form, source_name: e.target.value })}
          />
        </div>
        <div>
          <label className="label">Page cited on your site (optional)</label>
          <div className="relative">
            <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              placeholder="/about, /products/x"
              value={form.page_cited}
              onChange={(e) => setForm({ ...form, page_cited: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className="label">Observed date</label>
          <input
            className="input"
            type="date"
            value={form.observed_at}
            onChange={(e) => setForm({ ...form, observed_at: e.target.value })}
          />
        </div>
        <div className="md:col-span-2">
          <label className="label">Notes (optional)</label>
          <textarea
            className="input min-h-[60px]"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>

        {error && (
          <div className="md:col-span-2">
            <ErrorBanner body={error} />
          </div>
        )}

        <div className="md:col-span-2 flex gap-2 justify-end">
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={pending}>
            <Save className="w-4 h-4" />
            {pending ? 'Saving…' : initial ? 'Update citation' : 'Add citation'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ------------ Opportunities ----------- */

function Opportunities({ domain, hub }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [newOpp, setNewOpp] = useState({
    name: '',
    url: '',
    category: '',
    priority: 'medium',
  });

  const updateMutation = useMutation({
    mutationFn: (body) =>
      api
        .put(`/api/domains/${domain._id}/citations/hub`, body)
        .then((r) => r.data.hub),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['citation-dashboard', domain._id] }),
  });

  const opps = hub?.opportunities || [];
  const updateOpp = (id, patch) => {
    const next = opps.map((o) => (o.id === id ? { ...o, ...patch } : o));
    updateMutation.mutate({ opportunities: next });
  };
  const removeOpp = (id) => {
    const next = opps.filter((o) => o.id !== id);
    updateMutation.mutate({ opportunities: next });
  };
  const addOpp = () => {
    if (!newOpp.name.trim()) return;
    const next = [
      ...opps,
      {
        id: `cust-${Date.now()}`,
        ...newOpp,
        status: 'unclaimed',
        isSeed: false,
        notes: '',
      },
    ];
    updateMutation.mutate({ opportunities: next });
    setNewOpp({ name: '', url: '', category: '', priority: 'medium' });
    setAdding(false);
  };

  const byPriority = useMemo(() => {
    const order = { high: 0, medium: 1, low: 2 };
    return [...opps].sort((a, b) => (order[a.priority] || 9) - (order[b.priority] || 9));
  }, [opps]);

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Target className="w-4 h-4" />
          </span>
          Citation opportunities
        </div>
        <button className="btn-secondary" onClick={() => setAdding((v) => !v)}>
          <Plus className="w-4 h-4" /> Add target
        </button>
      </div>

      {adding && (
        <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <input
              className="input md:col-span-1"
              placeholder="Source name"
              value={newOpp.name}
              onChange={(e) => setNewOpp({ ...newOpp, name: e.target.value })}
            />
            <input
              className="input md:col-span-2"
              placeholder="https://signup.example.com"
              value={newOpp.url}
              onChange={(e) => setNewOpp({ ...newOpp, url: e.target.value })}
            />
            <select
              className="input"
              value={newOpp.priority}
              onChange={(e) => setNewOpp({ ...newOpp, priority: e.target.value })}
            >
              <option value="high">High priority</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <input
              className="input md:col-span-2"
              placeholder="Category (optional)"
              value={newOpp.category}
              onChange={(e) => setNewOpp({ ...newOpp, category: e.target.value })}
            />
            <div className="md:col-span-2 flex gap-2 justify-end">
              <button className="btn-secondary" onClick={() => setAdding(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={addOpp}>
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
          </div>
        </div>
      )}

      <ul className="divide-y divide-gray-50">
        {byPriority.map((o) => (
          <OpportunityRow
            key={o.id}
            opp={o}
            onChange={(patch) => updateOpp(o.id, patch)}
            onRemove={() => removeOpp(o.id)}
          />
        ))}
      </ul>
    </div>
  );
}

function OpportunityRow({ opp, onChange, onRemove }) {
  const meta = OPP_STATUS_META[opp.status] || OPP_STATUS_META.unclaimed;
  const priorityTone = {
    high: 'bg-rose-50 text-rose-700',
    medium: 'bg-amber-50 text-amber-700',
    low: 'bg-gray-100 text-gray-700',
  }[opp.priority];

  return (
    <li className="py-3 flex items-center justify-between gap-3 first:pt-0 last:pb-0">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span
          className={clsx(
            'flex-none h-9 w-9 rounded-lg flex items-center justify-center',
            opp.status === 'verified'
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-gray-100 text-gray-500',
          )}
        >
          {opp.status === 'verified' ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : (
            <Hash className="w-4 h-4" />
          )}
        </span>
        <div className="min-w-0">
          <div className="font-medium text-ink-900 flex items-center gap-2 flex-wrap">
            {opp.name}
            <span className={`pill ${priorityTone}`}>{opp.priority}</span>
            {opp.category && <span className="pill-muted">{opp.category}</span>}
          </div>
          {opp.url && (
            <a
              href={opp.url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-brand-600 hover:underline break-all inline-flex items-center gap-1"
            >
              {opp.url} <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-none">
        <select
          className={clsx(
            'text-xs rounded-lg px-2 py-1 border bg-white',
            meta.class,
            'border-current/30',
          )}
          value={opp.status}
          onChange={(e) => onChange({ status: e.target.value })}
        >
          {Object.entries(OPP_STATUS_META).map(([id, m]) => (
            <option key={id} value={id}>
              {m.label}
            </option>
          ))}
        </select>
        {!opp.isSeed && (
          <button
            className="btn-icon hover:!text-rose-600 hover:!bg-rose-50"
            onClick={onRemove}
            title="Remove"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </li>
  );
}

function hostname(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}
